class ImageWithTextSlideshow {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.autoplayObserver = null;
    this.resizeObserver = null;
    this.slideCount = 0;
    this.mountSlider();
    this.bindThemeEditorEvents();
  }

  bindThemeEditorEvents() {
    window.addEventListener('shopify:block:select', (event) => {
      if (!this.element.contains(event.target)) return;
      const slide = event.target.closest('[data-index]');
      if (!slide || !this.swiper) return;
      const index = parseInt(slide.dataset.index, 10) - 1;
      if (this.swiper.params.loop) {
        this.swiper.slideToLoop(index, 600);
      } else {
        this.swiper.slideTo(index, 600);
      }
    });
  }

  mountSlider() {
    if (this.swiper) return;

    this.slideCount = this.element.querySelectorAll('.swiper-slide').length;
    if (this.slideCount < 2) return;

    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', () => this.createSwiper(), { once: true });
      return;
    }

    this.createSwiper();
  }

  getPaginationEl() {
    const carousel = this.element.closest('.iwts-carousel');
    return carousel?.querySelector('.iwts-pagination') || this.element.querySelector('.swiper-pagination');
  }

  getSlideContentHeight(slideEl) {
    if (!slideEl) return 0;
    // Prefer the content container so loop/clone wrappers don't report 0.
    const content = slideEl.querySelector('.image-with-text-content-container') || slideEl;
    return Math.ceil(content.getBoundingClientRect().height);
  }

  applySlideHeight(slideEl) {
    if (!this.swiper || !slideEl) return;
    const height = this.getSlideContentHeight(slideEl);
    if (height < 1) return;

    this.swiper.el.style.height = `${height}px`;
    if (this.swiper.wrapperEl) {
      this.swiper.wrapperEl.style.height = `${height}px`;
    }
  }

  refreshAutoHeight() {
    if (!this.swiper) return;
    const activeSlide =
      this.swiper.slides?.[this.swiper.activeIndex] ||
      this.element.querySelector('.swiper-slide-active');
    this.applySlideHeight(activeSlide);
    this.swiper.updateAutoHeight(0);
    this.swiper.updateSize();
  }

  observeActiveSlide() {
    if (typeof ResizeObserver === 'undefined' || !this.swiper) return;

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = setTimeout(() => this.refreshAutoHeight(), 40);
      });
    }

    this.resizeObserver.disconnect();
    const activeSlide = this.swiper.slides?.[this.swiper.activeIndex];
    const target =
      activeSlide?.querySelector('.image-with-text-content-container') || activeSlide;
    if (target) this.resizeObserver.observe(target);
  }

  createSwiper() {
    if (this.swiper || typeof Swiper === 'undefined') return;

    const configuredSpeed = this.element.dataset.speed ? parseInt(this.element.dataset.speed, 10) : 400;
    // Native collection/product grids feel immediate; keep transitions snappy.
    const speed = Number.isFinite(configuredSpeed) ? Math.min(configuredSpeed, 450) : 400;
    const interval = this.element.dataset.interval ? parseInt(this.element.dataset.interval, 10) : 4000;
    const autoplayEnabled = this.element.dataset.autoplay === 'true';
    const effect = this.element.dataset.effect || 'slide';
    const paginationEl = this.getPaginationEl();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const swiperConfig = {
      spaceBetween: 0,
      // Loop clones break autoHeight on taller slides (e.g. wrapped vendor names).
      // Rewind still allows wrapping from last → first without clones.
      loop: false,
      rewind: true,
      effect,
      speed,
      autoHeight: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      simulateTouch: true,
      threshold: 4,
      touchRatio: 1.15,
      touchAngle: 45,
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.25,
      longSwipesMs: 250,
      followFinger: effect !== 'fade',
      resistanceRatio: 0.65,
      preventClicks: false,
      preventClicksPropagation: false,
      fadeEffect: {
        crossFade: true,
      },
      pagination: paginationEl ? { el: paginationEl, clickable: true } : undefined,
    };

    if (autoplayEnabled && !reducedMotion) {
      swiperConfig.autoplay = {
        delay: interval,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
        waitForTransition: false,
      };
    }

    this.swiper = new Swiper(this.element, swiperConfig);
    this.element.classList.add('iwts-swiper-ready');

    const scheduleLayoutRefresh = () => {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        this.refreshAutoHeight();
        this.observeActiveSlide();
      }, 40);
    };

    // Size to the destination slide *before* the transition paints, so taller
    // slides (Peter Schmid two-line heading) never overflow into the next section.
    this.swiper.on('slideChangeTransitionStart', () => {
      const nextSlide = this.swiper.slides?.[this.swiper.activeIndex];
      this.applySlideHeight(nextSlide);
      this.observeActiveSlide();
    });
    this.swiper.on('slideChange', scheduleLayoutRefresh);
    this.swiper.on('transitionEnd', () => {
      this.refreshAutoHeight();
      this.observeActiveSlide();
    });
    this.swiper.on('imagesReady', () => this.refreshAutoHeight());
    this.swiper.on('resize', scheduleLayoutRefresh);

    // Images / fonts can change slide height after first paint (esp. last trunk-show slide).
    this.element.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', scheduleLayoutRefresh, { once: true });
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.refreshAutoHeight()).catch(() => {});
    }

    requestAnimationFrame(() => {
      this.refreshAutoHeight();
      this.observeActiveSlide();
    });

    if (autoplayEnabled && !reducedMotion && this.swiper.autoplay) {
      this.bindAutoplayVisibility();
    }
  }

  bindAutoplayVisibility() {
    const section = this.element.closest('.section-image-with-text-slideshow');
    if (!section || !this.swiper?.autoplay) return;

    const syncAutoplay = (isVisible) => {
      if (!this.swiper?.autoplay) return;
      if (isVisible) {
        this.swiper.autoplay.start();
      } else {
        this.swiper.autoplay.stop();
      }
    };

    this.autoplayObserver = new IntersectionObserver(
      (entries) => syncAutoplay(Boolean(entries[0]?.isIntersecting)),
      { threshold: 0.2 }
    );

    this.autoplayObserver.observe(section);

    const rect = section.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    syncAutoplay(rect.top < vh * 0.8 && rect.bottom > vh * 0.2);
  }

  destroy() {
    clearTimeout(this.refreshTimer);
    this.autoplayObserver?.disconnect();
    this.autoplayObserver = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.element.classList.remove('iwts-swiper-ready');
  }
}

function initImageWithTextSlideshows(root = document) {
  root.querySelectorAll('.iwt-slideshow-swiper:not([data-iwts-mounted])').forEach((element) => {
    element.dataset.iwtsMounted = 'true';
    element._iwtsInstance = new ImageWithTextSlideshow(element);
  });

  if (typeof window.themeScrollRevealInit === 'function') {
    window.themeScrollRevealInit(root);
  }
}

function destroyImageWithTextSlideshows(root) {
  root.querySelectorAll('.iwt-slideshow-swiper[data-iwts-mounted]').forEach((element) => {
    element._iwtsInstance?.destroy();
    delete element.dataset.iwtsMounted;
    delete element._iwtsInstance;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initImageWithTextSlideshows());
} else {
  initImageWithTextSlideshows();
}

document.addEventListener('shopify:section:load', (event) => {
  initImageWithTextSlideshows(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyImageWithTextSlideshows(event.target);
});
